import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box, CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as groupApi from '../../../lib/api/cstGroup';
import { getComCodes } from '../../../lib/api/commonCode';

const EMPTY = {
  groupTyp: 'EPIC',
  groupNm: '',
  groupDesc: '',
  stDt: '',
  edDt: '',
  groupStatus: 'OPEN',
};

/**
 * 묶음을 새로 만들거나 고치는 창.
 *
 * 이름만 필수다. 기간이나 설명은 나중에 채워도 되게 뒀다 - 묶을 생각은 기회를 적다가
 * 나는데, 그때 열 칸을 다 채우라고 하면 그냥 안 만든다.
 */
const GroupFormDialog = ({ open, onClose, onSaved, group }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(EMPTY);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [saving, setSaving] = useState(false);

  const editing = Boolean(group?.groupUuid);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      groupTyp: group.groupTyp || 'EPIC',
      groupNm: group.groupNm || '',
      groupDesc: group.groupDesc || '',
      stDt: group.stDt || '',
      edDt: group.edDt || '',
      groupStatus: group.groupStatus || 'OPEN',
    } : EMPTY);
  }, [open, editing, group]);

  useEffect(() => {
    // 종류와 상태는 공통코드에서 온다. 이름을 바꾸고 싶으면 거기서 라벨만 고치면 된다.
    getComCodes({ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'GROUP_TYP', useYn: 'Y' })
      .then((r) => setTypes(r.data?.list || [])).catch(() => setTypes([]));
    getComCodes({ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'GROUP_STATUS', useYn: 'Y' })
      .then((r) => setStatuses(r.data?.list || [])).catch(() => setStatuses([]));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.groupNm.trim()) {
      enqueueSnackbar('묶음 이름을 입력해 주세요.', { variant: 'warning' });
      return;
    }
    if (form.stDt && form.edDt && form.stDt > form.edDt) {
      enqueueSnackbar('시작일이 종료일보다 늦습니다.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, groupNm: form.groupNm.trim() };
      const res = editing
        ? await groupApi.editGroup(group.groupUuid, body)
        : await groupApi.addGroup(body);
      enqueueSnackbar(editing ? '수정했습니다.' : '묶음을 만들었습니다.', { variant: 'success' });
      onSaved?.(res.data?.info);
      onClose?.();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? '묶음 수정' : '새 묶음'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="이름" value={form.groupNm} onChange={set('groupNm')}
            size="small" required autoFocus
            helperText="예: 연호엠에스 2026 상반기"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select label="종류" value={form.groupTyp} onChange={set('groupTyp')}
              size="small" sx={{ minWidth: 140 }}
            >
              {(types.length ? types : [{ revVal1: 'EPIC', revVal2: '에픽' }]).map((t) => (
                <MenuItem key={t.revVal1} value={t.revVal1}>{t.revVal2 || t.revVal1}</MenuItem>
              ))}
            </TextField>
            {editing && (
              <TextField
                select label="상태" value={form.groupStatus} onChange={set('groupStatus')}
                size="small" sx={{ minWidth: 140 }}
              >
                {(statuses.length ? statuses : [{ revVal1: 'OPEN', revVal2: '진행' }]).map((t) => (
                  <MenuItem key={t.revVal1} value={t.revVal1}>{t.revVal2 || t.revVal1}</MenuItem>
                ))}
              </TextField>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="시작일" type="date" value={form.stDt} onChange={set('stDt')}
              size="small" InputLabelProps={{ shrink: true }} fullWidth
            />
            <TextField
              label="종료일" type="date" value={form.edDt} onChange={set('edDt')}
              size="small" InputLabelProps={{ shrink: true }} fullWidth
            />
          </Box>
          <TextField
            label="설명" value={form.groupDesc} onChange={set('groupDesc')}
            size="small" multiline rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupFormDialog;
